import { request } from '@/api/client'
import {
  appScopeId,
  type AppScope,
  type RelatedApi,
  type ScopeCatalogItem,
  type ScopeIdentity,
  type ScopeStatus,
  type SensitivityLevel,
} from '@/types'

/** 权限页、开通弹窗每页条数。接口缺省也是 20，大于 100 会退回 20 */
export const SCOPE_PAGE_SIZE = 20

/** 全选时单次最多取这么多。再大接口会退回 20 */
export const SCOPE_SCAN_PAGE_SIZE = 100

export interface ScopePageQuery {
  page?: number
  page_size?: number
  keyword?: string
  category?: string
  identity_type?: ScopeIdentity
  /** 目录接口用来计算每一行的 granted。已开通列表不传 */
  app_id?: string
}

export interface OpenedScopeQuery extends ScopePageQuery {
  status?: ScopeStatus
}

export interface ScopePage<T> {
  list: T[]
  page: number
  page_size: number
  total: number
  /** 分类代码，筛选参数用这个 */
  categories: string[]
  /** 分类代码到当前语言名称，如 contacts → 通讯录 */
  categoryNames: Record<string, string>
}

export interface ScopeRef {
  scope_id: string
  identity_type: ScopeIdentity
}

export interface BatchCloseResult {
  succeeded: ScopeRef[]
  failed: ScopeRef[]
}

interface RelatedApiDto {
  name?: string
  type?: string
}

interface ScopeRowDto {
  scope_id?: string
  name?: string
  category?: string
  identity_type?: string
  risk_level?: number
  review_required?: boolean
  description?: string
  doc_url?: string
  related_apis?: RelatedApiDto[]
  status?: string | null
  reason?: string | null
  granted?: boolean
  in_live_snapshot?: boolean
}

interface ScopeListDto {
  list?: ScopeRowDto[]
  /** 当前身份下的分类全集，不随页码、已选分类、搜索词收窄 */
  categories?: string[]
  /** 分类代码到当前语言名称 */
  category_names?: Record<string, unknown>
  pagination?: {
    page?: number
    page_size?: number
    total?: number
  }
}

interface BatchCloseDto {
  succeeded?: { scope_id?: string; identity_type?: string }[]
  failed?: { scope_id?: string; identity_type?: string }[]
}

export function mapRiskLevel(level: number | undefined): SensitivityLevel {
  if (level === 3) return 'sensitive'
  if (level === 2) return 'elevated'
  return 'normal'
}

export function formatRelatedApi(api: RelatedApi): string {
  const prefix = api.type === 'event' ? '[事件]' : '[API]'
  return `${prefix} ${api.name}`
}

function mapRelatedApis(items: RelatedApiDto[] | undefined): RelatedApi[] {
  if (!items) return []
  return items.flatMap((item) => {
    const name = item.name?.trim() ?? ''
    if (!name) return []
    const type = item.type?.trim().toLowerCase() === 'event' ? 'event' : 'api'
    return [{ name, type }]
  })
}

function mapIdentity(value: string | undefined): ScopeIdentity | null {
  if (value === 'app' || value === 'user') return value
  return null
}

function mapStatus(value: string | null | undefined): ScopeStatus | null {
  if (value === 'approved' || value === 'pending' || value === 'rejected') return value
  return null
}

export function mapCatalogItem(dto: ScopeRowDto): ScopeCatalogItem | null {
  const identity_type = mapIdentity(dto.identity_type)
  const scope_id = dto.scope_id?.trim() ?? ''
  if (!identity_type || !scope_id) return null
  const doc_url = dto.doc_url?.trim()
  return {
    scope_id,
    name: dto.name?.trim() || scope_id,
    identity_type,
    category: dto.category?.trim() ?? '',
    sensitivity_level: mapRiskLevel(dto.risk_level),
    related_apis: mapRelatedApis(dto.related_apis),
    doc_url: doc_url || undefined,
    description: dto.description?.trim() || undefined,
    granted: dto.granted === true,
  }
}

export function mapOpenedScope(dto: ScopeRowDto): AppScope | null {
  const catalog = mapCatalogItem(dto)
  const status = mapStatus(dto.status)
  if (!catalog || !status) return null
  return {
    ...catalog,
    id: appScopeId(catalog.scope_id, catalog.identity_type),
    status,
    in_live_snapshot: dto.in_live_snapshot === true,
  }
}

function mapCategories(items: string[] | undefined): string[] {
  if (!items) return []
  return items.flatMap((item) => {
    const value = item.trim()
    return value ? [value] : []
  })
}

function mapCategoryNames(raw: Record<string, unknown> | undefined): Record<string, string> {
  if (!raw) return {}
  const names: Record<string, string> = {}
  for (const [key, value] of Object.entries(raw)) {
    const id = key.trim()
    const name = typeof value === 'string' ? value.trim() : ''
    if (id && name) names[id] = name
  }
  return names
}

function mapPage<T>(
  data: ScopeListDto | null | undefined,
  mapRow: (dto: ScopeRowDto) => T | null,
  fallback: { page: number; page_size: number },
): ScopePage<T> {
  const pagination = data?.pagination
  return {
    list: (data?.list ?? []).flatMap((row) => {
      const mapped = mapRow(row)
      return mapped ? [mapped] : []
    }),
    page: pagination?.page ?? fallback.page,
    page_size: pagination?.page_size ?? fallback.page_size,
    total: pagination?.total ?? 0,
    categories: mapCategories(data?.categories),
    categoryNames: mapCategoryNames(data?.category_names),
  }
}

/** 开发环境 StrictMode 会把加载效果跑两遍。相同列表请求还在飞时共用这一次 */
const listInflight = new Map<string, Promise<unknown>>()

function shareInflight<T>(key: string, load: () => Promise<T>): Promise<T> {
  const existing = listInflight.get(key)
  if (existing) return existing as Promise<T>
  const pending = load().finally(() => {
    if (listInflight.get(key) === pending) listInflight.delete(key)
  })
  listInflight.set(key, pending)
  return pending
}

function queryParams(query: OpenedScopeQuery, pageSize: number) {
  return {
    page: query.page ?? 1,
    page_size: query.page_size ?? pageSize,
    keyword: query.keyword?.trim() || undefined,
    category: query.category?.trim() || undefined,
    identity_type: query.identity_type,
    status: query.status,
  }
}

/** GET /api/v1/openapi/scopes。本地 /api 前缀会被代理剥掉，所以 path 里保留 /api/v1 */
export function listScopeCatalog(query: ScopePageQuery = {}) {
  const page = query.page ?? 1
  const page_size = query.page_size ?? SCOPE_PAGE_SIZE
  const params = {
    ...queryParams(query, page_size),
    app_id: query.app_id?.trim() || undefined,
  }
  return shareInflight(`catalog:${JSON.stringify(params)}`, () =>
    request<ScopeListDto | null>('/api/v1/openapi/scopes', { params }).then((data) =>
      mapPage(data, mapCatalogItem, { page, page_size }),
    ),
  )
}

const SCOPE_SCAN_MAX_PAGES = 100

/** 按接口返回的 total 翻页，直到拿全或遇到空页 */
export async function collectScopePages<T>(
  load: (page: number) => Promise<ScopePage<T>>,
): Promise<T[]> {
  const rows: T[] = []
  for (let page = 1; page <= SCOPE_SCAN_MAX_PAGES; page += 1) {
    const next = await load(page)
    rows.push(...next.list)
    if (next.list.length === 0 || rows.length >= next.total) break
  }
  return rows
}

/** 当前筛选下的全部目录行。只在全选时调用 */
export function listAllScopeCatalog(query: ScopePageQuery = {}) {
  return collectScopePages((page) =>
    listScopeCatalog({ ...query, page, page_size: SCOPE_SCAN_PAGE_SIZE }),
  )
}

/** GET /developer/apps/:appId/scopes。只含未删除的开通记录 */
export function listOpenedScopes(appId: string, query: OpenedScopeQuery = {}) {
  const page = query.page ?? 1
  const page_size = query.page_size ?? SCOPE_PAGE_SIZE
  const params = queryParams(query, page_size)
  return shareInflight(`opened:${appId}:${JSON.stringify(params)}`, () =>
    request<ScopeListDto | null>(`/developer/apps/${appId}/scopes`, { params }).then((data) =>
      mapPage(data, mapOpenedScope, { page, page_size }),
    ),
  )
}

/** 当前筛选下的全部开通记录。只在全选时调用 */
export function listAllOpenedScopes(
  appId: string,
  query: OpenedScopeQuery = {},
) {
  return collectScopePages((page) =>
    listOpenedScopes(appId, { ...query, page, page_size: SCOPE_SCAN_PAGE_SIZE }),
  )
}

/** POST /developer/apps/:appId/scopes。整批校验，任一失败整批不写 */
export function openAppScopes(appId: string, scopes: ScopeRef[]) {
  return request<ScopeListDto | null>(`/developer/apps/${appId}/scopes`, {
    method: 'POST',
    body: { scopes },
  })
}

/** POST /developer/apps/:appId/scopes/close */
export function closeAppScope(appId: string, scope: ScopeRef) {
  return request<unknown>(`/developer/apps/${appId}/scopes/close`, {
    method: 'POST',
    body: {
      scope_id: scope.scope_id,
      identity_type: scope.identity_type,
    },
  })
}

function mapRefs(
  items: { scope_id?: string; identity_type?: string }[] | undefined,
): ScopeRef[] {
  if (!items) return []
  return items.flatMap((item) => {
    const identity_type = mapIdentity(item.identity_type)
    const scope_id = item.scope_id?.trim() ?? ''
    if (!identity_type || !scope_id) return []
    return [{ scope_id, identity_type }]
  })
}

/** POST /developer/apps/:appId/scopes/batch-close。部分失败不回滚已成功的 */
export function batchCloseAppScopes(appId: string, scopes: ScopeRef[]) {
  return request<BatchCloseDto | null>(`/developer/apps/${appId}/scopes/batch-close`, {
    method: 'POST',
    body: { scopes },
  }).then((data) => ({
    succeeded: mapRefs(data?.succeeded),
    failed: mapRefs(data?.failed),
  }))
}
